/**
 * `make doctor`, printed.
 *
 * The four groups and every probe name under them come off
 * `internal/health/doctor.go` — `timed("connect+ping")`, `timed("quorum
 * queues")`, `timed("lease acquire/renew/release (Lua CAS)")`. A reader who
 * installs this and runs the command sees these lines.
 *
 * One row is a **warning**, and putting it on the page was the point of building
 * the figure this way. MongoDB here is standalone, so there are no transactions,
 * and the doctor says so rather than reporting a tick for a capability the
 * deployment does not have. A dependency check that cannot fail is not a check.
 */
import { Typewriter, Stagger } from '../../kit'
import { useT } from '../../i18n/context'
import { DOCTOR, BINARIES } from '../../data/doctor'
import './InstallTerminal.css'

const MARK = { ok: '✓', warn: '!', fail: '✗' } as const

export function InstallTerminal() {
  const t = useT()

  return (
    <div className="kl-install">
      <div className="kl-install__term k-mono">
        <div className="kl-install__cmd">
          <span className="kl-install__prompt">$</span>{' '}
          <Typewriter text="make doctor" cps={11} />
        </div>

        <Stagger className="kl-install__groups">
          {DOCTOR.map((group) => (
            <div key={group.name} className="kl-install__group">
              <div className={`kl-install__gline kl-install__gline--${group.status}`}>
                <span className="kl-install__mark">{MARK[group.status]}</span>
                <span className="kl-install__gname">{group.name}</span>
                <span className="kl-install__target">{group.target}</span>
              </div>
              {group.probes.map((probe) => (
                <div key={probe.name} className={`kl-install__probe kl-install__probe--${probe.status}`}>
                  <span className="kl-install__mark">{MARK[probe.status]}</span>
                  <span className="kl-install__pname">{probe.name}</span>
                  <span className="kl-install__ms">{probe.ms}ms</span>
                </div>
              ))}
            </div>
          ))}
        </Stagger>

        <div className="kl-install__bins">
          {BINARIES.map((b) => (
            <span key={b} className="kl-install__bin">
              {b}
            </span>
          ))}
        </div>
      </div>

      {/*
        The step that waits for you. `AccountModelAvailability` is written by
        nothing at install time, so with `RequireVerifiedModels` on there is no
        routable account/model pair until the first verification is recorded.
        Saying it here is cheaper than a reader discovering it as a bug.
      */}
      <p className="kl-install__first">{t('install.firstRun')}</p>
    </div>
  )
}

/**
 * The known gaps, on the marketing page.
 *
 * Every document in this repository ends with a section like this, and it is the
 * most trustworthy part of the documentation — the place that says *"Historical.
 * The argument is sound and the mechanism is gone."* Reproducing it here is not
 * modesty; it is the same claim the rest of the page makes, which is that what
 * Kimene tells you about itself is checkable.
 */
export function HonestGaps() {
  const t = useT()
  const gaps = [
    'install.gap.backup',
    'install.gap.nodeBootstrap',
    'install.gap.tls',
    'install.gap.retention',
    'install.gap.retry',
    'install.gap.terminal',
  ] as const

  return (
    <div className="kl-gaps">
      <h3 className="kl-gaps__title">{t('install.gapsTitle')}</h3>
      <p className="kl-gaps__sub">{t('install.gapsSub')}</p>
      <ul className="kl-gaps__list">
        {gaps.map((key) => (
          <li key={key}>{t(key)}</li>
        ))}
      </ul>
    </div>
  )
}
